import React, { useState, useEffect } from 'react';
import { database, firestore } from '../firebaseConfig';
import { ref, get, update, remove } from 'firebase/database';
import { doc, writeBatch } from 'firebase/firestore';
import { CLOUD_FUNCTION_URLS } from '../utils/cloudFunctions';
import './AiCleanerPopup.css';

const AiCleanerPopup = ({
  isOpen,
  bulkSelectedRows,
  bulkContactsData,
  userId,
  closeAiCleaner,
  onOpenAiManager,
  onOpenAutoSend
}) => {
  const [step, setStep] = useState('initial'); // initial, processing, emailValidation, confirm, missingRules
  const [aiSettings, setAiSettings] = useState({});
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedData, setProcessedData] = useState({
    toKeep: [],
    toRemove: [],
    toRename: []
  });
  const [openAiManagerAfter, setOpenAiManagerAfter] = useState(false);
  const [openAutoSendAfter, setOpenAutoSendAfter] = useState(false);
  const [selectedToKeep, setSelectedToKeep] = useState({});
  const [isConfirming, setIsConfirming] = useState(false); // Neuer State für den Bestätigungsvorgang
  
  // Neue States für E-Mail-Validierung
  const [emailValidationProgress, setEmailValidationProgress] = useState(0);
  const [emailValidationResults, setEmailValidationResults] = useState({
    validEmails: [],
    invalidEmails: [],
    totalProcessed: 0
  });
  
  // States für Section-Toggles
  const [isKeepSectionOpen, setIsKeepSectionOpen] = useState(false);
  const [isRenameSectionOpen, setIsRenameSectionOpen] = useState(false);
  const [isRemoveSectionOpen, setIsRemoveSectionOpen] = useState(false);

  // Funktion für dynamische Progress-Texte
  const getProgressText = (progress) => {
    if (progress <= 10) return "Getting started...";
    if (progress <= 25) return "Warming up...";
    if (progress <= 50) return "Making progress...";
    if (progress <= 75) return "Nearly there...";
    if (progress <= 95) return "Almost done...";
    return "Finishing up...";
  };

  // Funktion für E-Mail-Validierungs-Status
  const getEmailValidationText = () => {
    const { validEmails, invalidEmails, totalProcessed } = emailValidationResults;
    
    if (totalProcessed === 0) {
      return "Starting validation...";
    }
    
    if (invalidEmails.length === 0) {
      return `All ${totalProcessed} emails are valid so far`;
    }
    
    if (validEmails.length === 0) {
      return `${invalidEmails.length} emails failed validation`;
    }
    
    return `${validEmails.length} valid, ${invalidEmails.length} invalid`;
  };

  // Lade AI Settings
  useEffect(() => {
    const loadAiSettings = async () => {
      try {
        const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
        const snapshot = await get(aiSettingsRef);
        
        if (snapshot.exists()) {
          const settingsData = snapshot.val();
          setAiSettings(settingsData);
          
          // Prüfe, ob Cleanup-Regeln existieren
          if (!settingsData.cleanupGuidelines || settingsData.cleanupGuidelines.trim() === '') {
            setStep('missingRules');
            setLoading(false);
            return;
          }
        } else {
          // Keine Einstellungen gefunden
          setStep('missingRules');
          setLoading(false);
          return;
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der AI-Einstellungen:", error);
        setError("Fehler beim Laden der AI-Einstellungen");
        setLoading(false);
      }
    };
    
    loadAiSettings();
  }, [userId]);

  // Starte die Verarbeitung, wenn die Komponente geladen ist und die Einstellungen verfügbar sind
  useEffect(() => {
    if (!loading && !error && step === 'initial' && bulkContactsData.length > 0) {
      console.log('🚀 AI Cleaner: Starting processing with', bulkContactsData.length, 'contacts');
      startProcessing();
    }
  }, [loading, error, step, bulkContactsData]);

  // Hauptfunktion zum Analysieren und Filtern der Kontakte
  const startProcessing = async () => {
    setStep('processing');
    setProgress(0);
    
    try {
      const guidelines = aiSettings.cleanupGuidelines || '';
      const toKeep = [];
      const toRemove = [];
      const toRename = [];
      
      for (let i = 0; i < bulkContactsData.length; i++) {
        const contact = bulkContactsData[i];
        
        // Berechne den Fortschritt
        const currentProgress = Math.round(((i + 1) / bulkContactsData.length) * 100);
        setProgress(currentProgress);
        
        // Prüfe, ob die Kontaktdaten für die Analyse ausreichen
        if (!contact || (!contact.business_name && !contact.lead_description)) {
          toRemove.push({
            ...contact,
            reason: "Unzureichende Daten für die Analyse"
          });
          continue;
        }
        
        try {
          // Verwende die Cloud Function für die Analyse
          const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/cleanupContact', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              businessName: contact.business_name || '',
              description: contact.lead_description || '',
              guidelines: guidelines
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          
          const result = await response.json();
          
          if (result.shouldKeep) {
            // Wenn der Business-Name geändert werden soll
            if (result.cleanedBusinessName && result.cleanedBusinessName !== contact.business_name) {
              toRename.push({
                ...contact,
                original: contact.business_name,
                cleaned: result.cleanedBusinessName
              });
            } else {
              toKeep.push(contact);
            }
          } else {
            toRemove.push({
              ...contact,
              reason: result.reason || "Entspricht nicht den Kriterien"
            });
          }
        } catch (error) {
          console.error(`Fehler bei der Analyse von Kontakt ${contact.id}:`, error);
          // Bei Fehler behalten wir den Kontakt
          toKeep.push(contact);
        }
      }
      
      // Setze die Ergebnisse und gehe zum Bestätigungsschritt
      const newProcessedData = {
        toKeep: toKeep,
        toRemove: toRemove,
        toRename: toRename
      };
      setProcessedData(newProcessedData);
      
      // Initialisiere selectedToKeep mit allen Kontakten, die behalten werden sollen
      const initialSelectedToKeep = {};
      [...toKeep, ...toRename].forEach(contact => {
        initialSelectedToKeep[contact.id] = true;
      });
      setSelectedToKeep(initialSelectedToKeep);
      
      // Gehe direkt zur E-Mail-Validierung
      console.log('🚀 AI Cleaner: Switching to email validation step');
      console.log('🚀 Initial selected contacts:', initialSelectedToKeep);
      console.log('🚀 NEW PROCESSED DATA:', newProcessedData);
      setStep('emailValidation');
      startEmailValidation(initialSelectedToKeep, newProcessedData);
    } catch (error) {
      console.error("Fehler bei der Verarbeitung:", error);
      setError("Fehler bei der Verarbeitung der Kontakte");
      setStep('initial');
    }
  };

  // Funktion zum Umschalten der Auswahl für einen Kontakt
  const toggleContactSelection = (contactId) => {
    setSelectedToKeep(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  // Funktion zum Umschalten der Auswahl für alle Kontakte in einer Kategorie
  const toggleAllInCategory = (category, value) => {
    const newSelectedToKeep = { ...selectedToKeep };
    
    if (category === 'toKeep') {
      processedData.toKeep.forEach(contact => {
        newSelectedToKeep[contact.id] = value;
      });
    } else if (category === 'toRename') {
      processedData.toRename.forEach(contact => {
        newSelectedToKeep[contact.id] = value;
      });
    } else if (category === 'toRemove') {
      processedData.toRemove.forEach(contact => {
        newSelectedToKeep[contact.id] = value;
      });
    }
    
    setSelectedToKeep(newSelectedToKeep);
  };

  // Funktion zum Starten der E-Mail-Validierung
  const startEmailValidation = async (selectedToKeepParam = null, processedDataParam = null) => {
    setEmailValidationProgress(0);
    
    try {
      // Verwende den übergebenen Parameter oder den aktuellen State
      const currentSelectedToKeep = selectedToKeepParam || selectedToKeep;
      const currentProcessedData = processedDataParam || processedData;
      
      console.log('🚀 USING PROCESSED DATA:', currentProcessedData);
      
      // Sammle alle Kontakte, die ausgewählt sind (selectedToKeep === true)
      const contactsToValidate = [
        ...currentProcessedData.toKeep.filter(contact => currentSelectedToKeep[contact.id]),
        ...currentProcessedData.toRename.filter(contact => currentSelectedToKeep[contact.id]),
        ...currentProcessedData.toRemove.filter(contact => currentSelectedToKeep[contact.id])
      ];
      
      // Nur Kontakte mit E-Mail-Adressen
      const contactsWithEmails = contactsToValidate.filter(contact => 
        contact.lead_email && 
        contact.lead_email.trim() !== '' && 
        contact.lead_email.includes('@')
      );
      
      console.log(`🚀 EMAIL VALIDATION DEBUG:`);
      console.log(`- Total contacts to validate: ${contactsToValidate.length}`);
      console.log(`- Contacts with emails: ${contactsWithEmails.length}`);
      console.log('- All contacts:', contactsToValidate.map(c => ({
        id: c.id,
        business_name: c.business_name,
        lead_email: c.lead_email,
        hasEmail: !!c.lead_email
      })));
      console.log('- Contacts with valid emails:', contactsWithEmails.map(c => ({
        id: c.id,
        business_name: c.business_name,
        lead_email: c.lead_email
      })));
      
      if (contactsWithEmails.length === 0) {
        console.log('🚀 No contacts with emails found - skipping to confirm step');
        // Keine E-Mails zu validieren, gehe direkt zur Bestätigung
        setStep('confirm');
        return;
      }
      
      // Bereite die E-Mail-Daten für die Batch-Validierung vor
      const emailsToValidate = contactsWithEmails.map(contact => ({
        email: contact.lead_email,
        id: contact.id
      }));
      
      // Validiere E-Mails einzeln für bessere Progress-Tracking
      const validEmails = [];
      const invalidEmails = [];
      
      for (let i = 0; i < emailsToValidate.length; i++) {
        const emailData = emailsToValidate[i];
        
        // Aktualisiere den Fortschritt
        const currentProgress = Math.round(((i + 1) / emailsToValidate.length) * 100);
        setEmailValidationProgress(currentProgress);
        
        // Aktualisiere die Zwischenergebnisse
        setEmailValidationResults({
          validEmails: validEmails,
          invalidEmails: invalidEmails,
          totalProcessed: i + 1
        });
        
        try {
          // Validiere einzelne E-Mail
          const response = await fetch(CLOUD_FUNCTION_URLS.validateVerifyEmail(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: emailData.email
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          
          const result = await response.json();
          
          console.log('🚀 CLOUD FUNCTION RESPONSE:', result);
          console.log('🚀 RESULT.RESULT:', result.result);
          
          if (!result.success) {
            throw new Error(result.error || 'Unknown error during email validation');
          }
          
          // Füge Kontakt-ID hinzu
          const emailResult = {
            ...result.result,
            email: emailData.email,
            contactId: emailData.id
          };
          
          console.log('🚀 EMAIL RESULT:', emailResult);
          console.log('🚀 IS VALID:', emailResult.isValid);
          
          if (emailResult.isValid) {
            console.log('✅ Email is valid:', emailData.email);
            validEmails.push(emailResult);
          } else {
            console.log('❌ Email is invalid:', emailData.email, 'Reason:', emailResult.reason);
            invalidEmails.push(emailResult);
          }
          
        } catch (error) {
          console.error(`❌ Error validating email ${emailData.email}:`, error);
          console.error('❌ Error details:', error.message);
          // Bei Fehler behandeln wir die E-Mail als gültig (Service-Fehler ≠ ungültige E-Mail)
          validEmails.push({
            email: emailData.email,
            contactId: emailData.id,
            isValid: true,
            status: 'service_error',
            reason: 'Validation service temporarily unavailable'
          });
        }
        
        // Kleine Pause zwischen den Requests
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Finale Ergebnisse setzen
      setEmailValidationResults({
        validEmails: validEmails,
        invalidEmails: invalidEmails,
        totalProcessed: emailsToValidate.length
      });
      
      // Aktualisiere selectedToKeep - entferne invalide E-Mails
      const updatedSelectedToKeep = { ...currentSelectedToKeep };
      invalidEmails.forEach(emailResult => {
        if (emailResult.contactId) {
          updatedSelectedToKeep[emailResult.contactId] = false;
        }
      });
      setSelectedToKeep(updatedSelectedToKeep);
      
      // Verschiebe Kontakte mit invaliden E-Mails in die Remove-Liste
      const updatedProcessedData = { ...currentProcessedData };
      const invalidContactIds = invalidEmails.map(e => e.contactId).filter(Boolean);
      
      // Verarbeite invalide Kontakte
      invalidEmails.forEach(emailResult => {
        if (emailResult.contactId) {
          // Finde den ursprünglichen Kontakt
          const originalContact = contactsToValidate.find(c => c.id === emailResult.contactId);
          if (originalContact) {
            // Entferne aus der ursprünglichen Liste
            updatedProcessedData.toKeep = updatedProcessedData.toKeep.filter(contact => contact.id !== emailResult.contactId);
            updatedProcessedData.toRename = updatedProcessedData.toRename.filter(contact => contact.id !== emailResult.contactId);
            
            // Prüfe, ob er bereits in der Remove-Liste ist
            const alreadyInRemove = updatedProcessedData.toRemove.some(contact => contact.id === emailResult.contactId);
            if (!alreadyInRemove) {
              updatedProcessedData.toRemove.push({
                ...originalContact,
                reason: `Invalid email: ${emailResult.reason}`,
                emailValidationFailed: true
              });
            }
          }
        }
      });
      
      setProcessedData(updatedProcessedData);
      
      // Gehe zur Bestätigung
      setStep('confirm');
      
    } catch (error) {
      console.error("Fehler bei der E-Mail-Validierung:", error);
      setError("Fehler bei der E-Mail-Validierung: " + error.message);
      setStep('confirm'); // Gehe trotzdem zur Bestätigung, aber mit Fehlermeldung
    }
  };

  // Funktion zum Bestätigen und Anwenden der Änderungen
  const confirmChanges = async () => {
    setIsConfirming(true); // Starte den Bestätigungsprozess und zeige den Spinner
    
    try {
      const batch = writeBatch(firestore); // Firestore-Batch erstellen
      const contactsToDelete = [];
      
      // Verarbeite Umbenennungen
      processedData.toRename.forEach(contact => {
        if (selectedToKeep[contact.id]) {
          const docRef = doc(firestore, `World/${userId}/db/${contact.id}`);
          const updatedDescription = contact.lead_description 
            ? `${contact.lead_description}\\n\\nOriginal Business Name: ${contact.original}`
            : `Original Business Name: ${contact.original}`;
          
          batch.update(docRef, {
            business_name: contact.cleaned,
            lead_description: updatedDescription
          });
        } else {
          // Wenn nicht ausgewählt, zum Löschen markieren
          contactsToDelete.push(contact.id);
        }
      });
      
      // Markiere Kontakte zum Löschen
      processedData.toRemove.forEach(contact => {
        if (!selectedToKeep[contact.id]) {
          contactsToDelete.push(contact.id);
        }
      });
      
      // Füge Löschoperationen zum Batch hinzu
      contactsToDelete.forEach(contactId => {
        const docRef = doc(firestore, `World/${userId}/db/${contactId}`);
        batch.delete(docRef);
      });
      
      // Führe den Batch aus
      await batch.commit();
      
      // Speichere die Ergebnisse für mögliche weitere Verarbeitung
      const finalResults = {
        renamed: processedData.toRename.filter(contact => selectedToKeep[contact.id]).map(contact => ({
          id: contact.id,
          original: contact.original,
          cleaned: contact.cleaned,
          lead_description: (contact.lead_description ? `${contact.lead_description}\\n\\nOriginal Business Name: ${contact.original}` : `Original Business Name: ${contact.original}`)
        })),
        kept: processedData.toKeep.filter(contact => selectedToKeep[contact.id]),
        removed: contactsToDelete.length
      };
      
      // Schließe das Popup und übergebe die Ergebnisse
      closeAiCleaner(finalResults);
      
      // Sammle alle Kontakte, die behalten werden sollen (inkl. umbenannte)
      const contactsToKeep = [
        ...finalResults.kept,
        ...processedData.toRename.filter(contact => selectedToKeep[contact.id]),
        ...processedData.toRemove.filter(contact => selectedToKeep[contact.id])
      ];
      
      // Extrahiere IDs für die Weitergabe
      const contactIdsToKeep = contactsToKeep.map(contact => contact.id);
      
      // Schließe das Popup und öffne ggf. AI Manager oder AutoSend
      if (openAiManagerAfter && contactsToKeep.length > 0) {
        // Rufe die Callback-Funktion zum Öffnen des AI Managers auf
        if (onOpenAiManager) {
          onOpenAiManager(contactIdsToKeep, contactsToKeep);
        }
      } else if (openAutoSendAfter && contactsToKeep.length > 0) {
        // Rufe die Callback-Funktion zum Öffnen des AutoSend auf
        if (onOpenAutoSend) {
          onOpenAutoSend(contactIdsToKeep, contactsToKeep);
        }
      }
    } catch (error) {
      console.error("Fehler beim Anwenden der Änderungen:", error);
      setError("Fehler beim Anwenden der Änderungen");
      setIsConfirming(false); // Beende den Bestätigungsprozess bei Fehler
    }
  };

  // Funktion zum Öffnen der Einstellungsseite
  const navigateToSettings = () => {
    // Schließe den Cleaner
    closeAiCleaner();
    
    // Löse ein benutzerdefiniertes Event aus, um das AI-Tab in den Einstellungen zu aktivieren
    const settingsAiEvent = new CustomEvent('openSettingsTab', { 
      detail: { tab: 'ai' } 
    });
    document.dispatchEvent(settingsAiEvent);
    
    // Direkt zur WorldSettings-Komponente navigieren
    window.location.href = '/world/settings';
  };

  // Wenn das Popup nicht geöffnet ist, zeige nichts an
  if (!isOpen) return null;

  console.log('🚀 AI Cleaner: Rendering with step:', step, 'bulkContactsData:', bulkContactsData.length);

  return (
    <div className="ai-cleaner-popup-overlay">
      <div className="ai-cleaner-popup-container">
        <div className="ai-cleaner-popup-header">
          <h2><span className="salesgod-bold">SalesGod</span> <span className="cleaner-normal">Cleaner</span> <span className="ai-badge">AI</span></h2>
          <button className="ai-cleaner-close-btn" onClick={closeAiCleaner}>×</button>
        </div>
        
        <div className="ai-cleaner-popup-content">
          {loading && step === 'initial' && (
            <div className="ai-cleaner-loading">
              <div className="ai-cleaner-spinner"></div>
              <p>Loading settings...</p>
            </div>
          )}
          
          {error && (
            <div className="ai-cleaner-error">
              <p>{error}</p>
              <button onClick={closeAiCleaner}>Close</button>
            </div>
          )}
          
          {step === 'missingRules' && (
            <div className="ai-cleaner-missing-rules">
              <div className="ai-cleaner-icon-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h3>Cleanup Rules Not Configured</h3>
              <p>
                Please define the rules for contact cleanup in the settings first.
                Without these rules, the AI Cleaner cannot work effectively.
              </p>
              <div className="ai-cleaner-buttons">
                <button 
                  className="ai-cleaner-cancel-btn" 
                  onClick={closeAiCleaner}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          
          {step === 'processing' && (
            <div className="ai-cleaner-processing">
              <div className="ai-processing-grid"></div>
              
              {/* Runder Icon-Container */}
              <div className="ai-cleaner-icon-circle">
                <img src="/img/salesgod_dark.webp" alt="AI Cleaning" 
                  className="rotating-icon"
                  onError={(e) => {
                    // Fallback wenn das Bild nicht gefunden wird
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjEyIiBjeT0iMTIiIHI9IjEwIj48L2NpcmNsZT48cGF0aCBkPSJNMTYgMTJsLTQgNGw0IDQiPjwvcGF0aD48cGF0aCBkPSJNOCAxMmw0IDRsLTQgNCI+PC9wYXRoPjwvc3ZnPg==";
                  }}
                />
              </div>
              
              {/* Kontakt-Zähler */}
              <div className="ai-cleaner-counter">
                <span className="current">{Math.round(progress * bulkContactsData.length / 100)}</span>
                <span className="separator">of</span>
                <span className="total">{bulkContactsData.length}</span>
                <span>contacts analyzed</span>
              </div>
              
              <h3>Analyzing contacts...</h3>
              <div className="ai-cleaner-progress-container">
                <div 
                  className="ai-cleaner-progress-bar" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p>{getProgressText(progress)}</p>
            </div>
          )}
          
          {step === 'emailValidation' && (
            <div className="ai-cleaner-email-validation">
              <div className="ai-email-validation-grid"></div>
              
              {/* Runder Icon-Container für E-Mail-Validierung */}
              <div className="ai-cleaner-icon-circle email-validation">
                <img src="/img/salesgod_dark.webp" alt="Email Validation" 
                  className="rotating-icon"
                  onError={(e) => {
                    // Fallback wenn das Bild nicht gefunden wird
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjEyIiBjeT0iMTIiIHI9IjEwIj48L2NpcmNsZT48cGF0aCBkPSJNMTYgMTJsLTQgNGw0IDQiPjwvcGF0aD48cGF0aCBkPSJNOCAxMmw0IDRsLTQgNCI+PC9wYXRoPjwvc3ZnPg==";
                  }}
                />
              </div>
              
              {/* E-Mail-Validierungs-Zähler */}
              <div className="ai-cleaner-counter">
                <span className="current">{emailValidationResults.totalProcessed}</span>
                <span className="separator">of</span>
                <span className="total">{
                  [...processedData.toKeep, ...processedData.toRename, ...processedData.toRemove]
                    .filter(contact => selectedToKeep[contact.id] && contact.lead_email).length
                }</span>
                <span>emails validated</span>
              </div>
              
              <h3>Validating email addresses...</h3>
              <div className="ai-cleaner-progress-container">
                <div 
                  className="ai-cleaner-progress-bar email-validation" 
                  style={{ width: `${emailValidationProgress}%` }}
                ></div>
              </div>
              <p>{getEmailValidationText()}</p>
              
              {/* Zeige Zwischenergebnisse */}
     
            </div>
          )}
          
          {step === 'confirm' && (
            <div className="ai-cleaner-confirm">
              
              <div className="ai-cleaner-summary">
                <div className="ai-cleaner-summary-item keep">
                  <div className="ai-cleaner-summary-icon">
                    <img src="/img/done.svg" alt="Keep Icon" />
                  </div>
                  <span className="ai-cleaner-summary-count">{processedData.toKeep.length + processedData.toRename.length}</span>
                  <span className="ai-cleaner-summary-label">Keep</span>
                  <span className="ai-cleaner-summary-description">AI-verified with valid emails</span>
                </div>
                {processedData.toRename.length > 0 && (
                  <div className="ai-cleaner-summary-item rename">
                    <div className="ai-cleaner-summary-icon">
                      <img src="/img/wand-2.svg" alt="Rename Icon" />
                    </div>
                    <span className="ai-cleaner-summary-count">{processedData.toRename.length}</span>
                    <span className="ai-cleaner-summary-label">Rename</span>
                    <span className="ai-cleaner-summary-description">Business names cleaned up</span>
                  </div>
                )}
                <div className="ai-cleaner-summary-item remove">
                  <div className="ai-cleaner-summary-icon">
                    <img src="/img/danger.svg" alt="Remove Icon" />
                  </div>
                  <span className="ai-cleaner-summary-count">{processedData.toRemove.length}</span>
                  <span className="ai-cleaner-summary-label">Remove</span>
                  <span className="ai-cleaner-summary-description">AI-rejected or invalid emails</span>
                </div>
              </div>
              

              {/* Behalten-Sektion */}
              {processedData.toKeep.length > 0 && (
                <div className="ai-cleaner-section">
                  <div className="ai-cleaner-section-header" onClick={() => setIsKeepSectionOpen(!isKeepSectionOpen)}>
                    <div className="ai-cleaner-section-title">
                      <h4>
                        Contacts to Keep 
                        <span className="section-count keep">{processedData.toKeep.length}</span>
                      </h4>
                    </div>
                    {isKeepSectionOpen && (
                      <div className="ai-cleaner-select-all" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <input 
                            type="checkbox" 
                            checked={processedData.toKeep.every(contact => selectedToKeep[contact.id])}
                            onChange={(e) => toggleAllInCategory('toKeep', e.target.checked)}
                          />
                          Select all
                        </label>
                      </div>
                    )}
                    <div className={`ai-cleaner-toggle-icon ${isKeepSectionOpen ? 'open' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                  
                  {isKeepSectionOpen && (
                    <div className="ai-cleaner-contacts-list">
                      {processedData.toKeep.map(contact => (
                        <div key={contact.id} className="ai-cleaner-contact-item">
                          <div className="ai-cleaner-contact-select">
                            <input 
                              type="checkbox" 
                              checked={!!selectedToKeep[contact.id]} 
                              onChange={() => toggleContactSelection(contact.id)}
                            />
                          </div>
                          <div className="ai-cleaner-contact-details">
                            <div className="ai-cleaner-contact-name">
                              {contact.business_name || "No name"}
                            </div>
                            <div className="ai-cleaner-contact-description">
                              {contact.lead_description || "No description"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Umbenennen-Sektion */}
              {processedData.toRename.length > 0 && (
                <div className="ai-cleaner-section">
                  <div className="ai-cleaner-section-header" onClick={() => setIsRenameSectionOpen(!isRenameSectionOpen)}>
                    <div className="ai-cleaner-section-title">
                      <h4>
                        Contacts to Rename 
                        <span className="section-count rename">{processedData.toRename.length}</span>
                      </h4>
                    </div>
                    {isRenameSectionOpen && (
                      <div className="ai-cleaner-select-all" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <input 
                            type="checkbox" 
                            checked={processedData.toRename.every(contact => selectedToKeep[contact.id])}
                            onChange={(e) => toggleAllInCategory('toRename', e.target.checked)}
                          />
                          Select all
                        </label>
                      </div>
                    )}
                    <div className={`ai-cleaner-toggle-icon ${isRenameSectionOpen ? 'open' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                  
                  {isRenameSectionOpen && (
                    <div className="ai-cleaner-contacts-list">
                      {processedData.toRename.map(contact => (
                        <div key={contact.id} className="ai-cleaner-contact-item">
                          <div className="ai-cleaner-contact-select">
                            <input 
                              type="checkbox" 
                              checked={!!selectedToKeep[contact.id]} 
                              onChange={() => toggleContactSelection(contact.id)}
                            />
                          </div>
                          <div className="ai-cleaner-contact-details">
                            <div className="ai-cleaner-contact-name">
                              <span className="ai-cleaner-original">{contact.original}</span>
                              <span className="ai-cleaner-arrow">→</span>
                              <span className="ai-cleaner-cleaned">{contact.cleaned}</span>
                            </div>
                            <div className="ai-cleaner-contact-description">
                              {contact.lead_description || "No description"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Entfernen-Sektion */}
              {processedData.toRemove.length > 0 && (
                <div className="ai-cleaner-section">
                  <div className="ai-cleaner-section-header" onClick={() => setIsRemoveSectionOpen(!isRemoveSectionOpen)}>
                    <div className="ai-cleaner-section-title">
                      <h4>
                        Contacts to Remove 
                        <span className="section-count remove">{processedData.toRemove.length}</span>
                      </h4>
                    </div>
                    {isRemoveSectionOpen && (
                      <div className="ai-cleaner-select-all" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <input 
                            type="checkbox" 
                            checked={processedData.toRemove.every(contact => selectedToKeep[contact.id])}
                            onChange={(e) => toggleAllInCategory('toRemove', e.target.checked)}
                          />
                          Keep all
                        </label>
                      </div>
                    )}
                    <div className={`ai-cleaner-toggle-icon ${isRemoveSectionOpen ? 'open' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                  
                  {isRemoveSectionOpen && (
                    <>
                      {/* Separiere AI-rejected und Email-invalid */}
                      {processedData.toRemove.filter(contact => !contact.emailValidationFailed).length > 0 && (
                        <div className="ai-cleaner-subsection">
                          <h5 className="ai-cleaner-subsection-title">
                            <span className="ai-rejection-icon"><img src="/img/stamp.svg" alt="AI Rejected" style={{width: '20px', height: '20px'}}/></span>
                            AI-Rejected
                          </h5>
                          <div className="ai-cleaner-contacts-list">
                            {processedData.toRemove
                              .filter(contact => !contact.emailValidationFailed)
                              .map(contact => (
                                <div key={contact.id} className="ai-cleaner-contact-item ai-rejected">
                                  <div className="ai-cleaner-contact-select">
                                    <input 
                                      type="checkbox" 
                                      checked={!!selectedToKeep[contact.id]} 
                                      onChange={() => toggleContactSelection(contact.id)}
                                    />
                                  </div>
                                  <div className="ai-cleaner-contact-details">
                                    <div className="ai-cleaner-contact-name">
                                      {contact.business_name || "No name"}
                                    </div>
                                    <div className="ai-cleaner-contact-description">
                                      {contact.lead_description || "No description"}
                                    </div>
                                    <div className="ai-cleaner-contact-reason">
                                      {contact.reason}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* E-Mail-invalid Kontakte */}
                      {processedData.toRemove.filter(contact => contact.emailValidationFailed).length > 0 && (
                        <div className="ai-cleaner-subsection">
                          <h5 className="ai-cleaner-subsection-title">
                            <span className="email-invalid-icon"><img src="/img/stamp.svg" alt="Invalid Email" style={{width: '20px', height: '20px'}}/></span>
                            Invalid Email
                          </h5>
                          <div className="ai-cleaner-contacts-list">
                            {processedData.toRemove
                              .filter(contact => contact.emailValidationFailed)
                              .map(contact => (
                                <div key={contact.id} className="ai-cleaner-contact-item email-invalid">
                                  <div className="ai-cleaner-contact-select">
                                    <input 
                                      type="checkbox" 
                                      checked={!!selectedToKeep[contact.id]} 
                                      onChange={() => toggleContactSelection(contact.id)}
                                    />
                                  </div>
                                  <div className="ai-cleaner-contact-details">
                                    <div className="ai-cleaner-contact-name">
                                      {contact.business_name || "No name"}
                                      <span className="email-address">{contact.lead_email}</span>
                                    </div>
                                    <div className="ai-cleaner-contact-description">
                                      {contact.lead_description || "No description"}
                                    </div>
                                    <div className="ai-cleaner-contact-reason email-reason">
                                      {contact.reason}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              
              <div className="ai-cleaner-actions">
                <div className="ai-cleaner-next-action">
                  <p>Next step:</p>
                  <div className="ai-cleaner-next-options">
                    <label>
                      <input 
                        type="radio" 
                        name="nextAction" 
                        checked={!openAiManagerAfter && !openAutoSendAfter} 
                        onChange={() => {
                          setOpenAiManagerAfter(false);
                          setOpenAutoSendAfter(false);
                        }}
                      />
                      Update List
                    </label>
                    <label>
                      <input 
                        type="radio" 
                        name="nextAction" 
                        checked={openAiManagerAfter} 
                        onChange={() => {
                          setOpenAiManagerAfter(true);
                          setOpenAutoSendAfter(false);
                        }}
                      />
                      Open AI Manager
                    </label>
                    <label>
                      <input 
                        type="radio" 
                        name="nextAction" 
                        checked={openAutoSendAfter} 
                        onChange={() => {
                          setOpenAiManagerAfter(false);
                          setOpenAutoSendAfter(true);
                        }}
                      />
                      Open AutoSend
                    </label>
                  </div>
                </div>
                
                <div className="ai-cleaner-buttons">
                  <button 
                    className="ai-cleaner-cancel-btn" 
                    onClick={closeAiCleaner}
                    disabled={isConfirming}
                  >
                    Cancel
                  </button>
                  <button 
                    className={`ai-cleaner-confirm-btn ${isConfirming ? 'loading' : ''}`}
                    onClick={confirmChanges}
                    disabled={isConfirming}
                  >
                    {isConfirming && <span className="confirm-spinner"></span>}
                    Confirm Changes
                    {!isConfirming && (
                      <img 
                        src="/img/done.svg" 
                        alt="Confirm" 
                        className="confirm-icon"
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiCleanerPopup; 